import html2canvas from 'html2canvas';

/**
 * Capture a screenshot of the entire page or a specific element
 * @param {HTMLElement|string} element - DOM element or selector to capture (default: document.body)
 * @param {Object} options - html2canvas options
 * @returns {Promise<string>} - Base64 encoded image data URL
 */
export async function captureScreenshot(element = null, options = {}) {
  try {
    const targetElement = element 
      ? (typeof element === 'string' ? document.querySelector(element) : element)
      : document.body;

    if (!targetElement) {
      throw new Error('Element not found');
    }

    const canvas = await html2canvas(targetElement, {
      allowTaint: true,
      useCORS: true,
      logging: false,
      scale: 2, // Higher quality
      ...options
    });

    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error('Failed to capture screenshot:', error);
    throw error;
  }
}

/**
 * Download a screenshot as a PNG file
 * @param {HTMLElement|string} element - DOM element or selector to capture
 * @param {string} filename - Name of the file to download
 * @param {Object} options - html2canvas options
 */
export async function downloadScreenshot(element = null, filename = 'screenshot.png', options = {}) {
  try {
    const dataUrl = await captureScreenshot(element, options);
    
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Failed to download screenshot:', error);
    throw error;
  }
}

/**
 * Capture screenshot and convert to Blob
 * @param {HTMLElement|string} element - DOM element or selector to capture
 * @param {Object} options - html2canvas options
 * @returns {Promise<Blob>} - Image blob
 */
export async function captureScreenshotBlob(element = null, options = {}) {
  try {
    const dataUrl = await captureScreenshot(element, options);
    const response = await fetch(dataUrl);
    return await response.blob();
  } catch (error) {
    console.error('Failed to capture screenshot blob:', error);
    throw error;
  }
}

/**
 * Capture screenshot and upload to server
 * @param {HTMLElement|string} element - DOM element or selector to capture
 * @param {string} uploadUrl - Server endpoint to upload to
 * @param {Object} options - html2canvas options
 * @returns {Promise<Object>} - Upload response
 */
export async function captureAndUploadScreenshot(element = null, uploadUrl = '/api/screenshots', options = {}) {
  try {
    const blob = await captureScreenshotBlob(element, options);
    
    const formData = new FormData();
    formData.append('screenshot', blob, `screenshot-${Date.now()}.png`);
    formData.append('timestamp', new Date().toISOString());
    formData.append('url', window.location.href);
    formData.append('userAgent', navigator.userAgent);
    
    const response = await fetch(uploadUrl, {
      method: 'POST',
      body: formData
    });
    
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Failed to upload screenshot:', error);
    throw error;
  }
}
